import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FunnelCustomFieldDefinitionsGetResponse,
  FunnelCustomFieldDefinitionWithPosition,
} from 'src/api/generated';
import { CrmFunnelsCustomFieldDefinitionsService } from 'src/api/generated/services/CrmFunnelsCustomFieldDefinitionsService';
import { type FunnelCustomFieldDefinitionsUpdateBodyParams } from 'src/api/generated/models/FunnelCustomFieldDefinitionsUpdateBodyParams';
import { ApiResponse } from 'src/hooks/api/api';

interface UseFunnelCustomFieldDefinitionsResponse extends ApiResponse {
  definitions: FunnelCustomFieldDefinitionWithPosition[];
}

interface UseFunnelCustomFieldDefinitionIdsParams {
  funnelId?: number;
  enabled: boolean;
}

export const useFunnelCustomFieldDefinitions = ({
  funnelId,
  enabled = true,
}: UseFunnelCustomFieldDefinitionIdsParams): UseFunnelCustomFieldDefinitionsResponse => {
  const fetcher =
    async (): Promise<FunnelCustomFieldDefinitionsGetResponse> => {
      if (!funnelId) {
        return {
          definitions: [],
        };
      }
      return await CrmFunnelsCustomFieldDefinitionsService.getFunnelCustomFieldDefinitions(
        funnelId
      );
    };

  const {
    data,
    isLoading: loading,
    error,
    refetch,
  } = useQuery<FunnelCustomFieldDefinitionsGetResponse>({
    queryKey: ['crm/funnels/custom-field-definitions', funnelId],
    queryFn: fetcher,
    enabled,
  });

  return {
    loading,
    error,
    definitions: data?.definitions ?? [],
    refetch,
  };
};

interface UseAddDefinitionsToFunnelParams {
  onSuccess?: (res: void) => void;
  onError?: (error: Error) => void;
}

export const useAddDefinitionsToFunnel = (
  funnelId: number | undefined,
  params: UseAddDefinitionsToFunnelParams = {}
) => {
  const { onSuccess, onError } = params;
  const queryClient = useQueryClient();

  const mutate = async (definitionIds: number[]) => {
    if (!funnelId || definitionIds.length === 0) {
      return;
    }
    return await CrmFunnelsCustomFieldDefinitionsService.addFunnelCustomFieldDefinitions(
      funnelId,
      { definitionIds }
    );
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: mutate,
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ['crm/funnels/custom-field-definitions', funnelId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm/custom-fields/definitions'],
      });
      onSuccess?.(res);
    },
    onError,
  });

  return {
    addDefinitions: mutateAsync,
    loading: isPending,
    error: (error as Error) || null,
  };
};

interface UseReorderFunnelCustomFieldsParams {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useReorderFunnelCustomFieldDefinitions = (
  funnelId: number | undefined,
  params: UseReorderFunnelCustomFieldsParams = {}
) => {
  const { onSuccess, onError } = params;
  const queryClient = useQueryClient();

  const mutationFn = async (orderedDefinitionIds: number[]) => {
    if (!funnelId) return;
    const body: FunnelCustomFieldDefinitionsUpdateBodyParams = {
      definitionIds: orderedDefinitionIds,
    };
    // Use the replace-all endpoint; backend will persist order via positions
    return await CrmFunnelsCustomFieldDefinitionsService.updateFunnelCustomFieldDefinitions(
      funnelId,
      body
    );
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn,
    onMutate: async (orderedDefinitionIds: number[]) => {
      await queryClient.cancelQueries({
        queryKey: ['crm/funnels/custom-field-definitions', funnelId],
      });

      const prev =
        queryClient.getQueryData<FunnelCustomFieldDefinitionsGetResponse>([
          'crm/funnels/custom-field-definitions',
          funnelId,
        ]);

      if (prev) {
        const currentDefinitions = prev.definitions ?? [];

        const idToDef = new Map(currentDefinitions.map((d) => [d.id, d]));
        const reorderedDefinitions = orderedDefinitionIds
          .map((id) => idToDef.get(id))
          .filter(
            (
              d
            ): d is NonNullable<
              FunnelCustomFieldDefinitionsGetResponse['definitions']
            >[number] => Boolean(d)
          );

        const optimistic: FunnelCustomFieldDefinitionsGetResponse = {
          definitions: reorderedDefinitions,
        };

        queryClient.setQueryData(
          ['crm/funnels/custom-field-definitions', funnelId],
          optimistic
        );
      }

      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          ['crm/funnels/custom-field-definitions', funnelId],
          ctx.prev
        );
      }
      onError?.(_err as Error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['crm/funnels/custom-field-definitions', funnelId],
      });
      onSuccess?.();
    },
  });

  return {
    reorder: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};
